import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Lock, Mail, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soccer-pro-21/artifacts/6hs5jyaw_image-removebg-preview.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        formData,
        { withCredentials: true }
      );
      setUser(data);
      toast.success(`Welcome back, ${data.name}!`);
      // Redirect based on role
      const redirectPath = data.role === "team_manager" ? "/manager" : data.role === "big_screen_operator" ? "/big-screen" : "/admin";
      navigate(redirectPath, { replace: true, state: { user: data } });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4 relative overflow-hidden" data-testid="login-page">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-96 h-96 bg-[#FFC72C] rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#009E60] rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Back to home */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        {/* Logo & Title */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="ABFA" className="w-24 h-24 mx-auto mb-4" />
          <h1 className="font-heading text-3xl font-bold uppercase text-white mb-2">
            Admin Login
          </h1>
          <p className="text-gray-400 text-sm">
            Antigua & Barbuda Football Association
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {/* Email */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-12 py-6 rounded-none border-2 focus:border-[#FFC72C]"
                  placeholder="admin@abfa.ag"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-12 pr-12 py-6 rounded-none border-2 focus:border-[#FFC72C]"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-abfa-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              <span className="flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>Sign In</>
                )}
              </span>
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-8">
          © 2026 Antigua and Barbuda Football Association
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
